import { useEffect, useId, useMemo, useState } from "react";
import {
  columnLabels,
  columnLooksLikeTickers,
  columnTickers,
  readTickerCsv,
  type CsvRead,
} from "../../csvImport";

interface CsvImportStepProps {
  file: File;
  /** The column's symbols, as the user confirmed them. */
  onAdd: (symbols: string[]) => void;
  onCancel: () => void;
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * A client's ticker file, checked before anything is added: which column the
 * symbols are in, and whether the first row is a header. Both are guessed
 * (csvImport.ts) and shown with a preview, so a book exported with Ticker,
 * Name and Weight columns adds its tickers and nothing else.
 *
 * Every row of the chosen column goes in as a ticker, unchecked: the user
 * picked the column, so they've said what it holds. Shared by the builder's
 * Tickers box and the onboarding portfolio step.
 */
export default function CsvImportStep({ file, onAdd, onCancel }: CsvImportStepProps) {
  const [read, setRead] = useState<CsvRead | null>(null);
  const [failed, setFailed] = useState(false);
  const [column, setColumn] = useState(0);
  const [hasHeader, setHasHeader] = useState(false);
  const columnId = useId();
  const headerId = useId();

  useEffect(() => {
    let cancelled = false;
    setRead(null);
    setFailed(false);
    file
      .text()
      .then((text) => {
        if (cancelled) return;
        const next = readTickerCsv(text);
        setRead(next);
        setColumn(next.column);
        setHasHeader(next.hasHeader);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const labels = useMemo(() => (read ? columnLabels(read.rows, hasHeader) : []), [read, hasHeader]);
  const symbols = useMemo(
    () => (read ? columnTickers(read.rows, column, hasHeader) : []),
    [read, column, hasHeader]
  );

  const tickerColumn = columnLooksLikeTickers(symbols);
  // The first row reads as a header but would be added as a ticker.
  const headerAsTicker =
    !!read && !hasHeader && read.hasHeader && symbols.length > 0;
  const warning = symbols.length === 0
    ? "This column is empty."
    : !tickerColumn
      ? "These don’t look like tickers. Pick the column with the symbols."
      : headerAsTicker
        ? `The first row looks like a header, so “${symbols[0]}” would be added as a ticker.`
        : undefined;

  if (failed || (read && read.rows.length === 0)) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-gray-700 bg-gray-900 px-3 py-2.5 text-[13px] text-gray-300">
        <span className="flex-1">
          {failed ? `${file.name} couldn’t be read.` : `${file.name} has no rows.`} Upload a CSV
          with one ticker per row.
        </span>
        <StepButton onClick={onCancel}>Close</StepButton>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={`Add tickers from ${file.name}`}
      className="flex flex-col gap-2.5 rounded-md border border-gray-700 bg-gray-900 p-3"
    >
      <div className="flex items-baseline gap-2">
        <span className="truncate text-[13px] font-semibold text-gray-100">{file.name}</span>
        {read && (
          <span className="shrink-0 text-xs text-gray-400">
            {plural(hasHeader ? read.rows.length - 1 : read.rows.length, "row")}
          </span>
        )}
      </div>
      {!read ? (
        <p className="m-0 text-xs text-gray-400">Reading…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={columnId} className="text-[13px] text-gray-300">
              Tickers from column
            </label>
            <select
              id={columnId}
              value={column}
              onChange={(e) => setColumn(Number(e.target.value))}
              className="h-[30px] rounded-md border border-gray-600 bg-gray-800 py-0 pl-2 pr-8 text-[13px] text-gray-100 focus:border-squawk-blue focus:outline-none focus:ring-1 focus:ring-squawk-blue"
            >
              {labels.map((label, i) => (
                <option key={i} value={i}>
                  {label}
                </option>
              ))}
            </select>
            {symbols.length > 0 && (
              <span className="min-w-0 truncate text-[13px] font-semibold text-blue-200">
                {symbols.slice(0, 4).join(", ")}
                {symbols.length > 4 ? ", …" : ""}
              </span>
            )}
          </div>
          {warning && <p className="m-0 text-xs text-amber-300">{warning}</p>}
          <div className="flex items-center gap-2">
            <input
              id={headerId}
              type="checkbox"
              checked={hasHeader}
              onChange={(e) => setHasHeader(e.target.checked)}
              className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-2 focus:ring-squawk-blue/60 focus:ring-offset-0"
            />
            <label htmlFor={headerId} className="cursor-pointer text-[13px] text-gray-300">
              First row is a header
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={symbols.length === 0 || !tickerColumn}
              onClick={() => onAdd(symbols)}
              className="h-8 rounded-md border-0 bg-squawk-blue px-3.5 py-0 text-[13px] font-semibold text-gray-950 transition-colors hover:bg-squawk-blue/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add {plural(symbols.length, "ticker")}
            </button>
            <StepButton onClick={onCancel}>Cancel</StepButton>
          </div>
        </>
      )}
    </div>
  );
}

function StepButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-8 rounded-md border border-gray-600 bg-gray-800 px-3 py-0 text-[13px] font-medium text-gray-200 transition-colors hover:border-gray-500 hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/60"
    >
      {children}
    </button>
  );
}
